import { useEffect, useId, useRef, useState } from 'react';
import { T, useTheme, Ico, Badge, Input } from '../../../design';
import type { Product } from '../../../types/claims.types';
import { Thumb } from './shared';

export interface ProductSearchProps {
  value: Product | null;
  onSelect: (product: Product | null) => void;
  search: (query: string) => Promise<Product[]>;
}

export function ProductSearch({ value, onSelect, search }: ProductSearchProps) {
  const t = useTheme();
  const listId = useId();
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState<Product[]>([]);
  const [active, setActive] = useState(-1);
  const blurTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounced async søgning.
  useEffect(() => {
    if (value) return;
    const handle = setTimeout(async () => {
      const r = await search(q);
      setResults(r.slice(0, 8));
      setActive(-1);
    }, 220);
    return () => clearTimeout(handle);
  }, [q, value, search]);

  useEffect(() => () => { if (blurTimer.current) clearTimeout(blurTimer.current); }, []);

  function choose(p: Product) { onSelect(p); setOpen(false); setQ(''); setResults([]); }

  function onKeyDown(e: React.KeyboardEvent) {
    if (!open || results.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(results.length - 1, a + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(0, a - 1)); }
    else if (e.key === 'Enter' && active >= 0) { e.preventDefault(); choose(results[active]); }
    else if (e.key === 'Escape') { setOpen(false); }
  }

  if (value) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 12, border: `1.5px solid ${t.accent}`, borderRadius: t.radius, background: T.accentLight }}>
        <Thumb size={40} src={value.imageUrl} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.fg }}>{value.productName}</div>
          <div style={{ fontSize: 11.5, color: T.fg2, fontWeight: 600, marginTop: 2, display: 'flex', gap: 8 }}><span style={{ fontFamily: 'ui-monospace, monospace' }}>{value.sku}</span>·<span>{value.category}</span></div>
        </div>
        <button type="button" aria-label="Ryd produkt" onClick={() => { onSelect(null); setQ(''); }} style={{ border: 0, background: T.track, color: T.fg2, width: 30, height: 30, borderRadius: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}><Ico.x size={15} /></button>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      <Input
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => { blurTimer.current = setTimeout(() => setOpen(false), 160); }}
        onKeyDown={onKeyDown}
        placeholder="Søg navn eller varenummer…"
        icon={<Ico.search size={18} />}
        role="combobox"
        aria-expanded={open && results.length > 0}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={active >= 0 ? `${listId}-opt-${active}` : undefined}
      />
      {open && results.length > 0 && (
        <ul id={listId} role="listbox" style={{ position: 'absolute', top: 54, left: 0, right: 0, zIndex: 20, listStyle: 'none', margin: 0, padding: 0, background: T.surface, border: `1px solid ${T.border}`, borderRadius: t.radius, boxShadow: '0 12px 32px rgba(0,0,0,0.14)', overflow: 'hidden', maxHeight: 280, overflowY: 'auto' }}>
          {results.map((p, i) => (
            <li
              key={p.sku}
              id={`${listId}-opt-${i}`}
              role="option"
              aria-selected={i === active}
              onMouseDown={() => choose(p)}
              onMouseEnter={() => setActive(i)}
              style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '11px 13px', borderBottom: `1px solid ${T.borderLight}`, background: i === active ? T.accentLight : 'transparent', cursor: 'pointer' }}
            >
              <Thumb size={34} src={p.imageUrl} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 700, color: T.fg, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.productName}</div>
                <div style={{ fontSize: 11, color: T.fg3, fontWeight: 600, marginTop: 2 }}><span style={{ fontFamily: 'ui-monospace, monospace' }}>{p.sku}</span> · {p.category}</div>
              </div>
              {p.category === 'Elektronik' && <Badge tone="neutral" style={{ flexShrink: 0 }}>Elektronik</Badge>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
